import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '@/services/api';
import { EventOut, SectionOut, TableOut } from '@/types/api';
import Card from '@/components/atoms/Card';
import Button from '@/components/atoms/Button';
import BookingForm from '@/components/molecules/BookingForm';
import './EventTables.scss';

const EventTables: React.FC = () => {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventOut | null>(null);
  const [sections, setSections] = useState<SectionOut[]>([]);
  const [tables, setTables] = useState<TableOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedTable, setSelectedTable] = useState<TableOut | null>(null);
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);

  const getErrorMessage = useCallback((err: any) => {
    return err.message || t('errors.general');
  }, [t]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!eventId) return;
      
      try {
        setLoading(true);
        
        // Fetch event details
        const eventData = await apiService.getEvent(parseInt(eventId));
        setEvent(eventData);
        
        // Fetch sections for this event
        const sectionsData = await apiService.getSectionsByEvent(parseInt(eventId));
        setSections(sectionsData);
        
        // Fetch all tables for this event
        const tablesData = await apiService.getEventTables(parseInt(eventId));
        setTables(tablesData);
        
      } catch (err: any) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, [eventId, getErrorMessage]);

  const handleSectionSelect = useCallback((sectionId: number) => {
    setSelectedSection(sectionId);
  }, []);

  const handleTableSelect = useCallback((table: TableOut) => {
    setSelectedTable(table);
    setIsBookingFormOpen(true);
  }, []);

  const handleBookingSuccess = useCallback(() => {
    // Refresh tables to update availability
    if (eventId) {
      apiService.getEventTables(parseInt(eventId)).then(setTables);
    }
  }, [eventId]);

  const filteredTables = selectedSection 
    ? tables.filter(table => table.section_id === selectedSection)
    : tables;

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="error">
          <p>{error}</p>
          <Button 
            variant="primary" 
            onClick={() => window.location.reload()}
          >
            {t('common.retry')}
          </Button>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container">
        <div className="error">
          <p>{t('events.tables.eventNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="event-tables-page">
        <div className="event-tables-page__header">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate(`/events/${eventId}/venues/0`)}
          >
            ← {t('common.back')}
          </Button>
          <h1 className="event-tables-page__title">
            {t('events.tables.title', { eventName: event.name })}
          </h1>
        </div>

        <div className="event-tables-page__content">
          {sections.length === 0 ? (
            <div className="event-tables-page__empty">
              <p>{t('events.tables.noSections')}</p>
            </div>
          ) : (
            <>
              <div className="event-tables-page__sections">
                <h2 className="event-tables-page__section-title">
                  {t('events.tables.selectSection')}
                </h2>
                <div className="event-tables-page__section-grid">
                  {sections.map((section) => (
                    <Card 
                      key={section.id} 
                      variant={selectedSection === section.id ? 'elevated' : 'outlined'}
                      className={`section-card ${selectedSection === section.id ? 'section-card--selected' : ''}`}
                      onClick={() => handleSectionSelect(section.id)}
                    >
                      <div className="section-card__content">
                        <h3 className="section-card__title">{section.name}</h3>
                        {section.description && (
                          <p className="section-card__description">{section.description}</p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {selectedSection && (
                <div className="event-tables-page__tables">
                  <h2 className="event-tables-page__table-title">
                    {t('events.tables.selectTable')}
                  </h2>
                  <div className="event-tables-page__table-grid">
                    {filteredTables.length === 0 ? (
                      <div className="event-tables-page__empty">
                        <p>{t('events.tables.noTables')}</p>
                      </div>
                    ) : (
                      filteredTables.map((table) => (
                        <Card 
                          key={table.id} 
                          variant={table.is_active ? 'elevated' : 'outlined'}
                          className={`table-card ${!table.is_active ? 'table-card--unavailable' : ''}`}
                        >
                          <div className="table-card__content">
                            <h3 className="table-card__title">{table.name}</h3>
                            <p className="table-card__capacity">
                              {t('events.tables.capacity', { capacity: table.capacity })}
                            </p>
                            <span className={`table-card__status ${table.is_active ? 'table-card__status--available' : 'table-card__status--unavailable'}`}>
                              {table.is_active ? t('events.tables.available') : t('events.tables.unavailable')}
                            </span>
                            {table.is_active && (
                              <div className="table-card__actions">
                                <Button 
                                  variant="primary" 
                                  size="sm"
                                  onClick={() => handleTableSelect(table)}
                                >
                                  {t('events.tables.book')}
                                </Button>
                              </div>
                            )}
                          </div>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Booking Form Modal */}
      {selectedTable && event && (
        <BookingForm
          isOpen={isBookingFormOpen}
          onClose={() => {
            setIsBookingFormOpen(false);
            setSelectedTable(null);
          }}
          table={selectedTable}
          event={event}
          onBookingSuccess={handleBookingSuccess}
        />
      )}
    </div>
  );
};

export default EventTables; 